import React from "react";
import { Link2, X, Plus, ChevronDown } from "lucide-react";
import {
  Section,
  SectionHeader,
  SectionTitleGroup,
  SectionTitle,
  SectionBody,
  SegmentedControl,
  SegmentTab,
  SegmentLabel,
  AudienceChipRow,
  AudienceChip,
  ChipLabel,
  ChipRemoveBtn,
  AddAudienceChip,
  AddAudienceLabel,
  DataSourceLogoImg,
  DataSourceLogoPlaceholder,
  ModelDropdownWrapper,
  ModelDropdownList,
  ModelDropdownItem,
  SourceDropdown,
  DropdownLabel,
} from "../../organisms/StoryBuilder/StoryBuilder.styles";
import {
  Item as SourceItem,
  List as SourceList,
  Checkbox as SourceCheckbox,
} from "../MultiSelectSearch/MultiSelectSearch.styles";
import {
  ModalField,
  ModalFieldLabel,
} from "../StoryAudienceSection/StoryAudienceSection.styles";
import {
  AddSourceAnchor,
  AddSourcePill,
  BuilderSourceListPopover,
  ModalStack,
  ModelListPopover,
  SourceChipRow,
  SourceListPopover,
  SourcePill,
  SourcesChipBox,
} from "./StorySourcesSection.styles";
import { SourcesSkeleton } from "../../organisms/StoryBuilder/StoryBuilder.skeleton";
import type { StorySourcesSectionProps } from "./StorySourcesSection.types";

/**
 * Per-frame parts. The builder card and the modal field draw the same chips at
 * different radii and icon sizes, and place their popovers differently, so the
 * difference is a lookup rather than a second copy of the chip row.
 */
const FRAME = {
  builder: {
    Row: AudienceChipRow,
    Chip: AudienceChip,
    AddChip: AddAudienceChip,
    iconSize: 14,
    // The canvas has room below the field and nothing clipping it, so both
    // popovers keep the plain downward placement they have always had.
    SourcePopover: BuilderSourceListPopover,
    ModelPopover: ModelDropdownList,
  },
  modal: {
    Row: SourceChipRow,
    Chip: SourcePill,
    AddChip: AddSourcePill,
    iconSize: 16,
    // Inside the modal both popovers open upward and scroll themselves — see
    // `SourcesChipBox` in the styles for why.
    SourcePopover: SourceListPopover,
    ModelPopover: ModelListPopover,
  },
} as const;

export const StorySourcesSection: React.FC<StorySourcesSectionProps> = ({
  isPending = false,
  selectedSource,
  onSourceToggle,
  availableSources,
  selectedSourceIds,
  isAddingSource,
  onToggleAddingSource,
  sourceDropdownRef,
  allSourcesSelected,
  onSourceSelect,
  onSourceDeselect,
  onSelectAllSources,
  onDeselectAllSources,
  selectedModel,
  selectedModelLabel,
  modelOptions,
  onModelSelect,
  isModelOpen,
  onToggleModelOpen,
  modelDropdownRef,
  variant = "builder",
}) => {
  const { Row, Chip, AddChip, iconSize, SourcePopover, ModelPopover } =
    FRAME[variant];

  const tabs = (
    <SegmentedControl>
      <SegmentTab $active={selectedSource === "connectors"} type="button" onClick={() => onSourceToggle?.("connectors")}>
        <SegmentLabel $active={selectedSource === "connectors"}>Data connectors</SegmentLabel>
      </SegmentTab>
      <SegmentTab $active={selectedSource === "models"} type="button" onClick={() => onSourceToggle?.("models")}>
        <SegmentLabel $active={selectedSource === "models"}>Data models</SegmentLabel>
      </SegmentTab>
    </SegmentedControl>
  );

  const sourceChips = selectedSourceIds.map((id) => {
    const src = availableSources.find((s) => s.id === id);
    if (!src) return null;
    return (
      <Chip key={id}>
        {src.logoUrl
          ? <DataSourceLogoImg src={src.logoUrl} alt={src.name} />
          : <DataSourceLogoPlaceholder>{src.name?.[0]}</DataSourceLogoPlaceholder>}
        <ChipLabel>{src.name}</ChipLabel>
        <ChipRemoveBtn type="button" aria-label={`Remove ${src.name}`} onClick={() => onSourceDeselect?.(id)}>
          <X size={iconSize} />
        </ChipRemoveBtn>
      </Chip>
    );
  });

  const addSourceButton = (
    <AddChip type="button" aria-label="Add data source" onClick={onToggleAddingSource}>
      <Plus size={iconSize} />
      <AddAudienceLabel>Add data source</AddAudienceLabel>
    </AddChip>
  );

  // An empty workspace has nothing to offer, so the button toggles a flag that
  // opens nothing rather than an empty white box.
  const isSourceListOpen = isAddingSource && availableSources.length > 0;

  const sourceList = (
    <SourceList>
      <SourceItem onClick={() => allSourcesSelected ? onDeselectAllSources?.() : onSelectAllSources?.()}>
        <span style={{ flex: 1 }}>{allSourcesSelected ? "Unselect all" : "All"}</span>
        <SourceCheckbox $checked={allSourcesSelected} />
      </SourceItem>
      {availableSources.map((src) => {
        const selected = selectedSourceIds.includes(src.id);
        return (
          <SourceItem key={src.id} onClick={() => selected ? onSourceDeselect?.(src.id) : onSourceSelect?.(src.id)}>
            {src.logoUrl
              ? <DataSourceLogoImg src={src.logoUrl} alt={src.name} />
              : <DataSourceLogoPlaceholder>{src.name?.[0]}</DataSourceLogoPlaceholder>}
            <span style={{ flex: 1 }}>{src.name}</span>
            <SourceCheckbox $checked={selected} />
          </SourceItem>
        );
      })}
    </SourceList>
  );

  /**
   * The chip row with its popover, composed per frame.
   *
   * The two frames differ in *what the popover is anchored to*, which is why
   * this is not one shared tree: the builder anchors to the add-chip (it moves
   * as chips wrap, which is harmless on the canvas), the modal anchors to the
   * field box so wrapping cannot move it. The chips, the button and the list
   * itself are the same elements in both.
   */
  const connectorField =
    variant === "modal" ? (
      <SourcesChipBox ref={sourceDropdownRef} data-testid="sources-chip-box">
        <Row>
          {sourceChips}
          {addSourceButton}
        </Row>
        {isSourceListOpen && (
          <SourcePopover data-testid="sources-popover">
            {sourceList}
          </SourcePopover>
        )}
      </SourcesChipBox>
    ) : (
      <Row>
        {sourceChips}
        <AddSourceAnchor
          ref={sourceDropdownRef}
          data-testid="add-source-anchor"
        >
          {addSourceButton}
          {isSourceListOpen && (
            <SourcePopover data-testid="sources-popover">
              {sourceList}
            </SourcePopover>
          )}
        </AddSourceAnchor>
      </Row>
    );

  const modelPicker = (
    <ModelDropdownWrapper ref={modelDropdownRef}>
      <SourceDropdown type="button" aria-label="Select data model" onClick={onToggleModelOpen}>
        <DropdownLabel>{selectedModelLabel}</DropdownLabel>
        <ChevronDown size={20} />
      </SourceDropdown>
      {isModelOpen && modelOptions.length > 0 && (
        <ModelPopover data-testid="models-popover">
          {modelOptions.map((opt) => (
            <ModelDropdownItem key={opt.id} type="button" $active={opt.id === selectedModel}
              onClick={() => { onModelSelect?.(opt.id); onToggleModelOpen(); }}>
              {opt.label}
            </ModelDropdownItem>
          ))}
        </ModelPopover>
      )}
    </ModelDropdownWrapper>
  );

  if (variant === "modal") {
    // Figma 5326:88659 — label, tabs, then the picked tab's content inside the
    // same bordered box the audience field uses. The skeleton stays outside the
    // box: while sources are loading there is nothing to frame.
    return (
      <ModalField as="div" data-testid="section-sources">
        {/* Not a labelable control — the box holds tabs and buttons. */}
        <ModalFieldLabel as="span">Sources</ModalFieldLabel>
        {isPending ? (
          <SourcesSkeleton />
        ) : (
          <ModalStack>
            {tabs}
            {selectedSource === "connectors" ? connectorField : modelPicker}
          </ModalStack>
        )}
      </ModalField>
    );
  }

  return (
    <Section data-testid="section-sources">
      <SectionHeader>
        <SectionTitleGroup>
          <Link2 size={16} />
          <SectionTitle>Sources</SectionTitle>
        </SectionTitleGroup>
      </SectionHeader>
      <SectionBody style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {isPending ? (
          <SourcesSkeleton />
        ) : (
          <>
            {tabs}
            {selectedSource === "connectors" && connectorField}
            {selectedSource === "models" && modelPicker}
          </>
        )}
      </SectionBody>
    </Section>
  );
};

export default StorySourcesSection;
